import React, { useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import SlidePanel from '../shared/SlidePanel';
import siteSetting from '../../config/env';
import { pricingService, pricingMockActive } from '../../_services/pricing.service';
import { setUsage } from './usageStore';
import { hapticError, hapticSuccess } from '../../_utils/haptics';
import toast from '../shared/Toast';
import './planBilling.css';

/**
 * Add a card, in the app (Stripe Elements), for off-session charges:
 * settling messages, top-ups and the Beu + Bailey upgrade.
 *
 *   POST /billing/card/setup-intent → stripe.confirmCardSetup(clientSecret)
 *   → POST /billing/card/confirm { setupIntentId } → { usage }
 *
 * The card is saved on the caller's own customer (the owner's, or a
 * self-employed stylist's own). Opened from anywhere with openAddCard().
 */
const ADD_CARD_EVENT = 'beu:add-card';
export function openAddCard(opts = {}) {
    try { window.dispatchEvent(new CustomEvent(ADD_CARD_EVENT, { detail: { from: opts.from || '' } })); } catch (e) { /* old browser */ }
}
export function onAddCardRequest(fn) {
    const h = (e) => fn((e && e.detail) || {});
    window.addEventListener(ADD_CARD_EVENT, h);
    return () => window.removeEventListener(ADD_CARD_EVENT, h);
}

let stripePromise = null;
const stripeOnce = () => { if (!stripePromise) stripePromise = loadStripe(siteSetting.stripeKey); return stripePromise; };

/** Words for a card-setup failure. */
export function cardErrorText(e) {
    const code = e && (e.code || (e.data && e.data.code));
    if (code === 'setup_incomplete') return 'Your bank didn’t finish checking the card. Try again, or use another card.';
    if (code === 'not_yours' || (e && e.status === 403)) return 'That card setup belongs to someone else. Start again.';
    if (e && e.type === 'card_error' && e.message) return e.message;
    return 'The card couldn’t be saved just now. Try again in a moment.';
}

const CARD_STYLE = {
    style: {
        base: { fontSize: '16px', color: '#1A1A1E', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif', '::placeholder': { color: '#8E8E93' } },
        invalid: { color: '#B42318' },
    },
    hidePostalCode: false,
};

function Form({ stripe, elements, onClose, onSaved, sheet }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [complete, setComplete] = useState(false);
    const busyRef = useRef(false);

    const save = async () => {
        if (busyRef.current) return;
        busyRef.current = true; setBusy(true); setError('');
        try {
            const intent = await pricingService.cardSetupIntent();
            const setupIntentId = (intent && (intent.setupIntentId || intent.id)) || '';
            if (!pricingMockActive) {
                if (!stripe || !elements) throw new Error('stripe');
                const result = await stripe.confirmCardSetup(intent.clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
                if (result.error) { const err = new Error(result.error.message); err.type = result.error.type; throw err; }
            }
            const usage = await pricingService.cardConfirm(setupIntentId);
            if (usage) setUsage(usage);
            hapticSuccess();
            toast.success('Card saved');
            if (onSaved) onSaved(usage);
            if (sheet.current) sheet.current.handleClose(); else onClose();
        } catch (e) {
            hapticError();
            setError(cardErrorText(e));
        }
        busyRef.current = false; setBusy(false);
    };

    return (
        <div className="pb-addcard">
            <p className="pb-muted">For your Beu bill, settling messages and top-ups. It’s kept safely by Stripe; Beu never sees the full number.</p>
            <label className="pb-addcard-label" htmlFor="pb-card-el">Card</label>
            <div className="pb-addcard-field" id="pb-card-el">
                {pricingMockActive
                    ? <span className="pb-muted">Sample data: no card needed.</span>
                    : <CardElement options={CARD_STYLE} onChange={(ev) => { setComplete(!!ev.complete); if (ev.error) setError(ev.error.message); else setError(''); }} />}
            </div>
            {error && <p className="pb-error" role="alert">{error}</p>}
            <button type="button" className="pb-btn is-primary pb-addcard-save" disabled={busy || (!pricingMockActive && (!stripe || !complete))} onClick={save}>
                {busy ? 'Saving…' : 'Save card'}
            </button>
        </div>
    );
}

export default function AddCardPanel({ onClose, onSaved }) {
    const sheet = useRef(null);
    return (
        <SlidePanel ref={sheet} title="Add a card" onClose={onClose} width={460} className="pb-addcard-panel">
            <Elements stripe={pricingMockActive ? null : stripeOnce()}>
                <ElementsConsumer>
                    {({ stripe, elements }) => <Form stripe={stripe} elements={elements} onClose={onClose} onSaved={onSaved} sheet={sheet} />}
                </ElementsConsumer>
            </Elements>
        </SlidePanel>
    );
}
